/**
 * Pick content from a Content Library and link it into a unit — or into a Problem Bank.
 *
 * THE PARENT IS A PARAMETER, and that is the whole reason a Problem Bank works at all. The
 * platform's create call takes a `parent_locator`, and it does not care whether that is a
 * vertical or a container inside one: `cms/static/js/views/pages/container.js`
 * (`showSelectV2LibraryContent`) posts exactly `{library_content_key, category,
 * parent_locator: itemBankBlockId}` for its own problem-bank picker. So the same request
 * this dialog already made fills a bank, addressed one level deeper.
 *
 * Two panes: libraries on the left, that library's blocks on the right. A picker is a browsing
 * task, and a single list forced an author to leave and come back to change library — which is
 * exactly the move they make most while looking for something.
 *
 * The wording throughout says LINKED rather than "added", and the footer says what that means,
 * because the difference is the whole point and it is invisible once the block is in place. An
 * author who thinks they copied will be surprised when the library changes underneath them; one
 * who knows they linked will use it deliberately.
 *
 * Pagination is real here, not decorative: the blocks endpoint is always paginated, so a
 * library with two hundred components arrives twenty-four at a time. Search goes to the server
 * for the same reason — filtering the current page would silently search a twelfth of the
 * library and look like the rest was not there.
 */

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Badge, Banner, Button, Dialog, Icon, SearchField, Spinner } from '../../shared/ui';
import { componentIcon } from '../../shared/lib/unit';
import { getConfig } from '../../shared/lib/config';
import {
  assetWarnings,
  getLibraryDetail,
  libraryBlockPreviewUrl,
  linkLibraryBlock,
  listLibrariesForPicker,
  listLibraryBlocks,
  type LibraryBlockSummary,
} from '../../shared/lib/library-link';
import './library-picker.css';

const PAGE_SIZE = 24;

export function LibraryPicker({
  parentKey,
  destinationName,
  onClose,
  onLinked,
}: {
  /**
   * What the picked component becomes a child of: a unit, or a container inside one.
   * Sent verbatim as `parent_locator`.
   */
  parentKey: string;
  /**
   * The container's name, when the destination is not simply "this unit".
   *
   * Shown in the title because a Problem Bank picker opens over the unit page, and an author
   * with two banks in one unit has no other way to tell which one they are filling.
   */
  destinationName?: string;
  onClose: () => void;
  onLinked: () => void;
}) {
  const [libKey, setLibKey] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [preview, setPreview] = useState<LibraryBlockSummary | null>(null);
  const tenantSlug = getConfig().tenant.slug;

  const libraries = useQuery({
    queryKey: ['picker-libraries', tenantSlug],
    queryFn: () => listLibrariesForPicker(tenantSlug),
    staleTime: 60_000,
  });

  // Land in a library rather than on an empty right-hand pane. With one library — the common
  // case — the author never has to make a choice they had no alternative for.
  const chosen = libKey ?? libraries.data?.[0]?.id ?? null;

  // The list endpoint reports num_blocks: 0 for every library regardless of contents, so the
  // count shown for the SELECTED library comes from its detail record instead of being repeated
  // from the sidebar row.
  const detail = useQuery({
    queryKey: ['picker-library', chosen],
    queryFn: () => getLibraryDetail(chosen!),
    enabled: Boolean(chosen),
    staleTime: 60_000,
  });

  const blocks = useQuery({
    queryKey: ['picker-blocks', chosen, page, query],
    queryFn: () => listLibraryBlocks(chosen!, { page, pageSize: PAGE_SIZE, search: query || undefined }),
    enabled: Boolean(chosen),
  });

  const link = useMutation({
    mutationFn: (block: LibraryBlockSummary) => linkLibraryBlock(parentKey, block),
    onSuccess: (result) => {
      // Asset clashes are reported but not fatal, so the dialog stays open to show them
      // rather than closing over a warning the author never sees.
      if (assetWarnings(result.static_file_notices).length === 0) {
        onLinked();
        onClose();
      } else {
        onLinked();
      }
    },
  });

  const warnings = link.isSuccess ? assetWarnings(link.data?.static_file_notices) : [];
  const rows = blocks.data?.results ?? [];
  const pages = blocks.data?.num_pages ?? 1;

  return (
    <Dialog
      title={destinationName ? `Add to ${destinationName}` : 'Add from a library'}
      wide
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Done
          </Button>
          <span className="libpick__note">
            <Icon name="link" />
            Linked content stays connected to the library, so updates can flow through later.
          </span>
        </>
      }
    >
      {libraries.isLoading ? (
        <Spinner label="Loading libraries…" />
      ) : libraries.isError ? (
        <Banner tone="danger">
          <strong>Could not load your libraries.</strong> The authoring service did not respond.
          Close this and try again; nothing has been changed.
        </Banner>
      ) : (libraries.data ?? []).length === 0 ? (
        <Banner tone="info">
          <strong>There are no content libraries yet.</strong> Libraries hold components you want
          to reuse across courses. Create one under Content Library, add some components to it,
          and they will appear here.
        </Banner>
      ) : (
        <div className="libpick">
          <nav className="libpick__libs" aria-label="Libraries">
            {(libraries.data ?? []).map((library) => (
              <button
                type="button"
                key={library.id}
                className={`libpick__lib${library.id === chosen ? ' is-active' : ''}`}
                onClick={() => {
                  setLibKey(library.id);
                  setPage(1);
                  setQuery('');
                }}
              >
                <span className="libpick__lib-name">{library.title}</span>
                <span className="libpick__lib-meta">{library.org}</span>
              </button>
            ))}
          </nav>

          <div className="libpick__body">
            {detail.data && (
              <div className="libpick__head">
                <span>
                  <strong>{detail.data.title}</strong>
                  {detail.data.description && <small>{detail.data.description}</small>}
                </span>
                <span className="libpick__head-meta">
                  {detail.data.num_blocks}{' '}
                  {detail.data.num_blocks === 1 ? 'component' : 'components'}
                  {detail.data.has_unpublished_changes && ' · unpublished changes'}
                </span>
              </div>
            )}

            <div className="libpick__search">
              <SearchField
                value={query}
                onChange={(value) => {
                  setQuery(value);
                  setPage(1);
                }}
                placeholder="Search this library…"
              />
            </div>

            {link.isError && (
              <Banner tone="danger">
                <strong>That component was not added.</strong> The platform refused the link, so
                nothing was created. This usually means the library block has never been
                published — publish the library and try again.
              </Banner>
            )}

            {warnings.length > 0 && (
              <Banner tone="warning">
                <strong>Added, with notes about its files.</strong>
                <ul className="libpick__warnings">
                  {warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </Banner>
            )}

            {blocks.isLoading ? (
              <Spinner label="Loading components…" />
            ) : blocks.isError ? (
              <Banner tone="danger">
                <strong>Could not load this library&rsquo;s components.</strong> Pick another
                library, or close this and try again.
              </Banner>
            ) : rows.length === 0 ? (
              <p className="libpick__empty">
                {query ? (
                  `Nothing in this library matches “${query}”.`
                ) : (
                  <>
                    <strong>This library has no components yet.</strong>
                    {/* The picker opens on whichever library comes first, which may not be the
                        one holding the content — say so, rather than leaving an author to
                        conclude there is nothing anywhere. */}
                    <br />
                    {(libraries.data ?? []).length > 1
                      ? 'Choose another library on the left, or add components to this one under Content Library.'
                      : 'Add components to it under Content Library, and publish them — linking pulls the published version.'}
                  </>
                )}
              </p>
            ) : (
              <ul className="libpick__blocks">
                {rows.map((block) => (
                  <li key={block.id} className="libpick__block">
                    <span className="libpick__block-icon" data-type={block.block_type}>
                      <Icon name={componentIcon(block.block_type)} />
                    </span>
                    <span className="libpick__block-body">
                      <strong>{block.display_name || block.id}</strong>
                      <span className="libpick__block-meta">
                        <span className="mono">{block.block_type}</span>
                        {/* A never-published block cannot be linked: the sync reads the
                            published version, so saying so up front beats a 400 later. */}
                        {!block.last_published ? (
                          <Badge tone="warning">Not published</Badge>
                        ) : (
                          block.has_unpublished_changes && (
                            <Badge tone="neutral">Draft changes not published</Badge>
                          )
                        )}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="libpick__preview-btn"
                      disabled={!block.last_published}
                      aria-label={`Preview ${block.display_name || block.id}`}
                      onClick={() => setPreview(preview?.id === block.id ? null : block)}
                    >
                      <Icon name={preview?.id === block.id ? 'visibility_off' : 'visibility'} />
                    </button>
                    <Button
                      busy={link.isPending && link.variables?.id === block.id}
                      disabled={!block.last_published || link.isPending}
                      onClick={() => link.mutate(block)}
                    >
                      <Icon name="add_link" />
                      Add
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            {/* Rendered by the platform, published version — the same thing that will be
                inserted. An author choosing between four similarly-named blocks needs to see
                them, not their titles. */}
            {preview && (
              <div className="libpick__preview">
                <div className="libpick__preview-head">
                  <span>
                    Preview: <strong>{preview.display_name || preview.id}</strong>
                  </span>
                  <button type="button" onClick={() => setPreview(null)}>
                    <Icon name="close" />
                    Hide
                  </button>
                </div>
                <iframe
                  key={preview.id}
                  className="libpick__preview-frame"
                  src={libraryBlockPreviewUrl(preview.id)}
                  title={`Preview of ${preview.display_name || preview.id}`}
                />
              </div>
            )}

            {pages > 1 && (
              <div className="libpick__pager">
                <Button
                  variant="ghost"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => current - 1)}
                >
                  <Icon name="chevron_left" />
                  Previous
                </Button>
                <span>
                  Page {blocks.data?.current_page ?? page} of {pages}
                  {blocks.data?.count !== undefined && ` · ${blocks.data.count} components`}
                </span>
                <Button
                  variant="ghost"
                  disabled={page >= pages}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Next
                  <Icon name="chevron_right" />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
}
